import { _isNotEmpty, _notNil } from '@/littledash';
import { use, useState, type FC } from 'react';
import { SendFdaRuleTextsContext } from '@/components/Agents/SendValidator/utils/sendFdaRuleTexts';
import styles from './sendResults.module.scss';

interface SendFdaValidatorIdsProps {
  ids: ReadonlyArray<string>;
}

/**
 * The FDA validator rules that also check a finding's rule (SD0002, SE0056): the namespace commercial
 * validators report in, so a disagreement can be reconciled in it. A triage note, never a severity. Each
 * id opens the FDA's own message and description, credited, once the report has fetched them; until then,
 * or if that failed, the ids show alone. Renders nothing when there are none.
 */
const SendFdaValidatorIds: FC<SendFdaValidatorIdsProps> = ({ ids }) => {
  const { texts, credit } = use(SendFdaRuleTextsContext);
  const [open, setOpen] = useState<string | null>(null);
  if (!_isNotEmpty(ids)) {
    return null;
  }
  const shown = _notNil(open) ? texts.get(open) : undefined;

  return (
    <span className={styles.fdaIds} data-test-component="SendFdaValidatorIds">
      <span className={styles.fdaIdsLabel}>FDA validator:</span>
      {ids.map((id) =>
        texts.has(id) ? (
          <button
            key={id}
            type="button"
            className={styles.fdaId}
            aria-expanded={open === id}
            onClick={() => setOpen((current) => (current === id ? null : id))}
            data-test-element="fda-id"
            data-test-key={id}
          >
            {id}
          </button>
        ) : (
          <span key={id} className={styles.fdaIdText} data-test-element="fda-id" data-test-key={id}>
            {id}
          </span>
        )
      )}
      {_notNil(shown) && (
        <span className={styles.fdaText} data-test-element="fda-text" data-test-key={shown.id}>
          <span className="db fw6">{shown.message}</span>
          <span className="db">{shown.description}</span>
          {_notNil(credit) && (
            <span className="db mid-gray mt1" data-test-element="fda-credit">
              {credit.documents.map((document) => `${document.title} ${document.version}`).join(' and ')},{' '}
              {credit.publisher}. {credit.statement}
            </span>
          )}
        </span>
      )}
    </span>
  );
};

export default SendFdaValidatorIds;
